import type { Signal } from './conciergeState'
import { SUBJECT_PRONOUNS } from './tapsOptions.data'

type SignalText = Pick<Signal<string>, 'value' | 'display'>

// What makes a bare occasion read as a reason once it follows "it's for": a
// possessive for the recipient's own milestones, "our" for a shared one, an
// article for the sentiments. Keyed by canonical value, so a buyer-typed
// occasion (no canonical) and the holidays (proper nouns that read fine bare —
// "it's for Christmas") pass through untouched.
const OCCASION_FRAMING: Partial<Record<string, 'a' | 'our' | 'the' | 'possessive'>> = {
  birthday: 'possessive',
  housewarming: 'possessive',
  anniversary: 'our',
  'thank you': 'a',
  congratulations: 'a',
  'new year': 'the',
}

const POSSESSIVES = { she: 'her', he: 'his' } as const

// Display first: the tapped word carries the gender ("wife", "daughter") where
// its canonical ("spouse", "child") doesn't.
export function subjectPronoun (relationship: SignalText): 'she' | 'he' | null {
  return SUBJECT_PRONOUNS[relationship.display ?? ''] ?? SUBJECT_PRONOUNS[relationship.value ?? ''] ?? null
}

// The occasion phrased as the reason for the gift — "her birthday", "our
// anniversary", "a thank you" — or null when none is set. Every prefix is a word
// the parser skips over, so a sentence built with this still canonicalizes when
// the free-text input re-parses it.
export function occasionPhrase (occasion: SignalText, relationship: SignalText): string | null {
  const words = occasion.display ?? occasion.value
  if (!words) return null

  // Only the bare canonical term gets framed. A typed occasion keeps the buyer's own wording as
  // its display even when it resolves to a canonical ("our anniversary" → anniversary), and that
  // wording already reads as a reason — framing it again would give "our our anniversary".
  const bare = occasion.value !== null && words.toLowerCase() === occasion.value
  const framing = bare ? OCCASION_FRAMING[words.toLowerCase()] : undefined
  if (!framing) return words
  if (framing !== 'possessive') return `${framing} ${words}`

  const pronoun = subjectPronoun(relationship)
  return `${pronoun ? POSSESSIVES[pronoun] : 'their'} ${words}`
}
